import React, { useState, useEffect, useCallback } from 'react';
import { CreditCard, Loader2, Download, Calendar, ArrowUpDown, Filter } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import html2pdf from 'html2pdf.js';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const fmt = (cents) => `$${(cents / 100).toLocaleString('en-US', { minimumFractionDigits: 2 })}`;

const PaymentHistoryReport = () => {
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [methodFilter, setMethodFilter] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [sort, setSort] = useState({ field: 'date', dir: 'desc' });
  const printRef = React.useRef(null);

  useEffect(() => {
    const now = new Date();
    const past = new Date(now);
    past.setDate(past.getDate() - 90);
    setToDate(now.toISOString().slice(0, 10));
    setFromDate(past.toISOString().slice(0, 10));
  }, []);

  const generate = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (fromDate) params.set('from_date', fromDate);
      if (toDate) params.set('to_date', toDate);
      if (methodFilter) params.set('method', methodFilter);
      const res = await fetch(`${API_URL}/api/reports/payment-history?${params}`, { credentials: 'include' });
      if (!res.ok) throw new Error('Failed to load report');
      setData(await res.json());
    } catch (err) { toast.error(err.message); }
    finally { setLoading(false); }
  }, [fromDate, toDate, methodFilter]);

  const toggleSort = (field) => {
    setSort(prev => prev.field === field ? { field, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { field, dir: 'desc' });
  };

  const sortedRows = data ? [...data.rows].sort((a, b) => {
    let cmp = 0;
    if (sort.field === 'date') cmp = a.date.localeCompare(b.date);
    else if (sort.field === 'customer_name') cmp = a.customer_name.localeCompare(b.customer_name);
    else if (sort.field === 'amount') cmp = a.amount - b.amount;
    else if (sort.field === 'method') cmp = a.method.localeCompare(b.method);
    return sort.dir === 'desc' ? -cmp : cmp;
  }) : [];

  const handlePDF = async () => {
    if (!printRef.current) return;
    setExporting(true);
    try {
      await html2pdf().set({
        margin: [10, 10, 10, 10],
        filename: `Payment_History_${fromDate}_${toDate}.pdf`,
        html2canvas: { scale: 2 },
        jsPDF: { unit: 'mm', format: 'a4', orientation: 'landscape' },
      }).from(printRef.current).save();
      toast.success('PDF downloaded');
    } catch { toast.error('PDF export failed'); }
    finally { setExporting(false); }
  };

  const cols = [
    { key: 'date', label: 'Date', align: 'left', sortable: true },
    { key: 'customer_name', label: 'Customer', align: 'left', sortable: true },
    { key: 'payment_number', label: 'Payment #', align: 'left' },
    { key: 'method', label: 'Method', align: 'left', sortable: true },
    { key: 'reference', label: 'Reference', align: 'left' },
    { key: 'amount', label: 'Amount', align: 'right', sortable: true },
  ];

  return (
    <>
      <style>{`
        @media print {
          body * { visibility: hidden; }
          [data-testid="payment-history-print"], [data-testid="payment-history-print"] * { visibility: visible; }
          [data-testid="payment-history-print"] { position: absolute; left: 0; top: 0; width: 100%; padding: 20px; }
          [data-testid="no-print"] { display: none !important; }
        }
      `}</style>

      <div className="space-y-6" data-testid="payment-history-page">
        {/* Controls */}
        <div className="bg-white rounded-lg border p-6" data-testid="no-print">
          <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <CreditCard className="w-5 h-5 text-[#8B7355]" />
            Payment History Report
          </h2>
          <div className="flex items-end gap-4 flex-wrap">
            <div>
              <label className="text-sm font-medium text-gray-700 mb-1 block">From</label>
              <div className="relative">
                <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)}
                  className="pl-9 pr-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-[#8B7355]/30 focus:border-[#8B7355]"
                  data-testid="payment-from-date" />
              </div>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700 mb-1 block">To</label>
              <div className="relative">
                <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input type="date" value={toDate} onChange={e => setToDate(e.target.value)}
                  className="pl-9 pr-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-[#8B7355]/30 focus:border-[#8B7355]"
                  data-testid="payment-to-date" />
              </div>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700 mb-1 block">Method</label>
              <div className="relative">
                <Filter className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <select value={methodFilter} onChange={e => setMethodFilter(e.target.value)}
                  className="pl-9 pr-8 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-[#8B7355]/30 focus:border-[#8B7355] appearance-none bg-white"
                  data-testid="payment-method-filter">
                  <option value="">All Methods</option>
                  {data && data.available_methods.map(m => <option key={m} value={m}>{m}</option>)}
                </select>
              </div>
            </div>
            <Button onClick={generate} disabled={loading} className="bg-[#8B7355] hover:bg-[#6B5B4A] text-white" data-testid="payment-generate-btn">
              {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <CreditCard className="w-4 h-4 mr-2" />}
              Generate Report
            </Button>
            {data && (
              <>
                <Button variant="outline" size="sm" onClick={() => window.print()} data-testid="payment-print-btn">Print</Button>
                <Button variant="outline" size="sm" onClick={handlePDF} disabled={exporting} data-testid="payment-pdf-btn">
                  {exporting ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Download className="w-4 h-4 mr-1" />} PDF
                </Button>
              </>
            )}
          </div>

          {/* Quick Periods */}
          <div className="mt-4 flex gap-2 flex-wrap">
            {[
              { label: 'Last 30 Days', days: 30 },
              { label: 'Last 90 Days', days: 90 },
              { label: 'This Month', preset: 'month' },
              { label: 'Last Month', preset: 'last-month' },
              { label: 'This Year', preset: 'year' },
            ].map(p => (
              <button key={p.label} data-testid={`payment-quick-${p.label.toLowerCase().replace(/\s+/g, '-')}`}
                onClick={() => {
                  const now = new Date();
                  let from, to;
                  if (p.days) { to = now; from = new Date(now); from.setDate(from.getDate() - p.days); }
                  else if (p.preset === 'month') { from = new Date(now.getFullYear(), now.getMonth(), 1); to = now; }
                  else if (p.preset === 'last-month') { from = new Date(now.getFullYear(), now.getMonth() - 1, 1); to = new Date(now.getFullYear(), now.getMonth(), 0); }
                  else if (p.preset === 'year') { from = new Date(now.getFullYear(), 0, 1); to = now; }
                  setFromDate(from.toISOString().slice(0, 10));
                  setToDate(to.toISOString().slice(0, 10));
                }}
                className="px-3 py-1.5 text-xs rounded-full border border-[#8B7355]/30 text-[#8B7355] hover:bg-[#8B7355]/10 transition-colors">
                {p.label}
              </button>
            ))}
          </div>
        </div>

        {/* Report */}
        {data && (
          <div ref={printRef} data-testid="payment-history-print" className="bg-white rounded-lg border" style={{ fontFamily: 'Arial, sans-serif' }}>
            <div className="flex justify-between items-start p-8 pb-4">
              <div>
                <h1 className="text-2xl font-bold text-[#8B7355]">Payment History</h1>
                <p className="text-sm text-gray-500 mt-1">{data.period.from} to {data.period.to} | {data.payment_count} payment{data.payment_count !== 1 ? 's' : ''}</p>
              </div>
              <div className="text-right">
                <p className="text-xs text-gray-400 uppercase tracking-wider">Total Received</p>
                <p className="text-2xl font-bold text-green-600" data-testid="payment-total-amount">{fmt(data.total_amount)}</p>
              </div>
            </div>

            <div className="px-8 pb-4">
              <table className="w-full text-sm border-collapse" data-testid="payment-history-table">
                <thead>
                  <tr className="bg-[#f5f5f0] border-y">
                    {cols.map(col => (
                      <th key={col.key}
                        className={`px-3 py-2 font-semibold text-gray-700 text-${col.align} ${col.sortable ? 'cursor-pointer select-none' : ''}`}
                        onClick={col.sortable ? () => toggleSort(col.key) : undefined}>
                        <span className="flex items-center gap-1" style={{ justifyContent: col.align === 'right' ? 'flex-end' : 'flex-start' }}>
                          {col.label}
                          {col.sortable && <ArrowUpDown className="w-3 h-3 text-gray-400" />}
                        </span>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {sortedRows.map((row, i) => (
                    <tr key={i} className="border-b hover:bg-gray-50/60" data-testid={`payment-row-${i}`}>
                      <td className="px-3 py-2">{row.date}</td>
                      <td className="px-3 py-2 font-medium">{row.customer_name}</td>
                      <td className="px-3 py-2 font-mono text-xs">{row.payment_number}</td>
                      <td className="px-3 py-2">
                        <span className="px-2 py-0.5 text-xs rounded-full bg-gray-100 text-gray-700">{row.method}</span>
                      </td>
                      <td className="px-3 py-2 text-gray-500">{row.reference || '—'}</td>
                      <td className="px-3 py-2 text-right font-semibold text-green-600">{fmt(row.amount)}</td>
                    </tr>
                  ))}
                  {sortedRows.length === 0 && (
                    <tr><td colSpan={6} className="px-3 py-6 text-center text-gray-400">No payments in this period</td></tr>
                  )}
                  {sortedRows.length > 0 && (
                    <tr className="bg-[#f5f5f0] border-t-2 border-gray-300 font-bold">
                      <td colSpan={5} className="px-3 py-2 text-right">Total</td>
                      <td className="px-3 py-2 text-right text-green-600">{fmt(data.total_amount)}</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Method Summary */}
            {Object.keys(data.method_totals).length > 0 && (
              <div className="px-8 pb-6">
                <h3 className="text-sm font-bold text-gray-700 uppercase tracking-wider mb-3">Summary by Method</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="payment-method-summary">
                  {Object.entries(data.method_totals).sort((a, b) => b[1] - a[1]).map(([method, total]) => (
                    <div key={method} className="border rounded-lg p-3 text-center" data-testid={`method-${method.toLowerCase().replace(/\s+/g, '-')}`}>
                      <p className="text-xs font-medium text-gray-500">{method}</p>
                      <p className="text-lg font-bold mt-1">{fmt(total)}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="px-8 pb-6">
              <p className="text-xs text-gray-400">Report generated on {new Date().toLocaleDateString()} | Amounts in USD</p>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default PaymentHistoryReport;
